import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useWorkspaceId } from "../bridges/session";
import { useRefreshLegacyUser } from "../bridges/settings";
import { api } from "../data/client";
import { scopedKey } from "../data/keys";

/* ---------- types: the wire contract, docs/redesign/plans/2026-10-28-settings-contact-api.md ---------- */

export type Place = { text: string | null; lat: number | null; lng: number | null; place_id?: string | null };
export type RoleLabel = "Owner" | "Manager" | "Team member" | "Volunteer";

export type SettingsPageData = {
  profile: { first_name: string; last_name: string; phone: string | null; email: string; language: "en" | "he" | "fr"; viewed_as: boolean; has_password?: boolean };
  support_access: { on: boolean };
  /** The server's shortest password (8 today, 10 once the release sets it). */
  password_min_length?: number;
  workspace: { id: number; name: string; is_personal: boolean; is_owner: boolean; country: string | null; role_label: RoleLabel };
  can: {
    account_settings: boolean; rename: boolean; pause_or_close: boolean; offline_data: boolean;
    categories: boolean; projects: boolean; become_volunteer: boolean; stop_volunteering: boolean;
  };
  account: null | {
    address: Place;
    map_center: { name: string | null; lat: number | null; lng: number | null };
    split: boolean;
    route_start: Place;
    manual: { enabled: boolean; include_in_routes: boolean; region: string | null; region_state?: string | null; region_country?: string | null; filled_in_automatically: string | null };
    contacts: { visit_due_soon_days: number; visit_recent_days: number; new_switch: boolean };
    counts: { categories: number; projects: number };
    setup_pending: boolean;
  };
  volunteering: { is_volunteer: boolean; commitments: number };
};

/* ---------- keys and addresses ---------- */

const A = "/api/auth";
const N = `${A}/next`;
export const URLS = {
  page: `${N}/settings/page`,
  account: `${N}/settings/account`,
  /** The contract lists no write for name and phone: this one is asked of the backend (see the report). */
  profile: `${N}/settings/profile`,
  userData: `${A}/set-user-data`,
  password: `${A}/password`,
  passwordLink: `${N}/settings/password-link`,
  support: `${A}/support-access-approval`,
  workspace: `${A}/workspace/settings`,
};
export const pageKey = (ws: number | null) => scopedKey(ws, "settings", "page");

/* ---------- read ---------- */

/** Everything Settings shows, and what the person may do, in one read. */
export const useSettingsPage = (enabled = true) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: pageKey(ws),
    queryFn: async () => (await api.get<{ data: SettingsPageData }>(URLS.page)).data,
    enabled: ws !== null && enabled,
    staleTime: 15_000,
    retry: false,
  });
};

/* ---------- writes: each one reads the page again (the server is the truth) and tells the old pages ---------- */

const useAfter = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const legacy = useRefreshLegacyUser();
  return () => { legacy(); return client.invalidateQueries({ queryKey: pageKey(ws) }); };
};

export type ProfileInput = { first_name: string; last_name: string; phone: string | null };
export const useSaveProfile = () => {
  const after = useAfter();
  return useMutation({ mutationFn: async (v: ProfileInput) => { await api.patch(URLS.profile, v); }, onSuccess: after });
};

/** Changes the screen at once, then saves it for the person. */
export const useSaveLanguage = () => {
  const { i18n } = useTranslation();
  const after = useAfter();
  return useMutation({
    mutationFn: async (language: "en" | "he" | "fr") => { await i18n.changeLanguage(language); await api.post(URLS.userData, { language }); },
    onSuccess: after,
  });
};

export type PasswordInput = { password: string; password_confirmation: string; current_password?: string };
/** M3.4: "Forgot it? Send me a link": a reset link to the person's own address. */
export const useSendPasswordLink = () => useMutation({ mutationFn: async () => { await api.post(URLS.passwordLink); } });

export const useSavePassword = () => {
  const after = useAfter();
  return useMutation({ mutationFn: async (v: PasswordInput) => { await api.put(URLS.password, v); }, onSuccess: after });
};

export const useSaveSupportAccess = () => {
  const after = useAfter();
  return useMutation({ mutationFn: async (on: boolean) => { await api.put(URLS.support, { support_access_approved: on }); }, onSuccess: after });
};

/** M2.5: ends this person's sign-ins on every OTHER device; this one stays signed in. */
export const useSignOutEverywhere = () => useMutation({ mutationFn: async () => { await api.post("/api/auth/next/me/sign-out-everywhere"); } });

/** Owner only. */
export const useRenameAccount = () => {
  const after = useAfter();
  return useMutation({ mutationFn: async (name: string) => { await api.patch(URLS.account, { name }); }, onSuccess: after });
};

/** Today's workspace settings write: addresses, hand-typed switches, the "New" switch. Only the keys given are sent. */
export const useSaveWorkspace = () => {
  const after = useAfter();
  return useMutation({ mutationFn: async (v: Record<string, unknown>) => { await api.patch(URLS.workspace, v); }, onSuccess: after });
};
